import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Pagination,
  Paper,
  Divider,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";

import { getUserTransactions } from "../../../api/api";

export default function UserTransactionsDialog({
  open,
  onClose,
  user,
}) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterType, setFilterType] = useState("all");
  const [cycleDate, setCycleDate] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [currentBalance, setCurrentBalance] = useState({
    planned: 0,
    unplanned: 0,
    total: 0,
  });

  // =========================================================
  // LOAD
  // =========================================================

  useEffect(() => {
    if (!open || !user) {
      return;
    }

    load(1);
  }, [open, user, filterType, cycleDate]);

  const load = async (pageNum) => {
    if (!user) {
      return;
    }

    setLoading(true);

    try {
      const params = {
        page: pageNum,
        limit: pageSize,
        filter_type: filterType,
      };

      if (user?.user_id) {
        params.user_id = user.user_id;
      } else if (user?.emp_code) {
        params.emp_code = user.emp_code;
      }

      if (cycleDate) {
        params.cycle_date = cycleDate;
      }

      const data = await getUserTransactions(params);

      const result = Array.isArray(data?.transactions)
        ? data.transactions
        : [];

      setTransactions(result);

      setTotalCount(Number(data?.pagination?.total_count || 0));
      setTotalPages(Number(data?.pagination?.total_pages || 0));

      // =====================================================
      // CURRENT BALANCE FROM BACKEND
      // =====================================================

      if (data?.current_balance) {
        setCurrentBalance({
          planned: Number(data.current_balance.planned || 0),
          unplanned: Number(data.current_balance.unplanned || 0),
          total: Number(data.current_balance.total || 0),
        });
      } else {
        // Fallback to balance page data
        const planned = Number(user?.planned_balance || 0);
        const unplanned = Number(user?.unplanned_balance || 0);

        setCurrentBalance({
          planned,
          unplanned,
          total: Number(user?.total_balance) || planned + unplanned,
        });
      }

      setPage(pageNum);
    } catch (error) {
      console.error("Failed to load user transactions:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FILTER
  // =========================================================

  const handleFilterChange = (event, newFilter) => {
    if (newFilter === null) {
      return;
    }

    setFilterType(newFilter);
    setPage(1);
  };

  const handleCycleDateChange = (event) => {
    setCycleDate(event.target.value);
    setPage(1);
  };

  const clearCycle = () => {
    setCycleDate("");
    setPage(1);
  };

  // =========================================================
  // PAGINATION
  // =========================================================

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    load(newPage);
  };

  // =========================================================
  // HELPERS
  // =========================================================

  const formatDate = (date) => {
    if (!date) {
      return "—";
    }

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
      return "—";
    }

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isInteger(number) ? number : number.toFixed(2);
  };

  const getBucketColor = (bucket) => {
    return bucket === "unplanned" ? "warning" : "primary";
  };

  const getTransactionTypeColor = (type) => {
    const value = type?.toLowerCase() || "";

    if (value.includes("credit")) {
      return "success";
    }

    if (value.includes("debit")) {
      return "error";
    }

    if (value.includes("approved")) {
      return "success";
    }

    return "default";
  };

  // =========================================================
  // TRANSACTION SUMMARY
  // =========================================================

  const transactionSummary = useMemo(() => {
    let credits = 0;
    let debits = 0;

    transactions.forEach((txn) => {
      const type = txn.transaction_type?.toLowerCase() || "";
      const days = Number(txn.days || 0);

      if (type.includes("credit")) {
        credits += days;
      }

      if (type.includes("debit")) {
        debits += days;
      }
    });

    return {
      credits,
      debits,
      net: credits - debits,
    };
  }, [transactions]);

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: { xs: "90vh", md: "70vh" },
        },
      }}
    >
      {/* ===================================================
          TITLE
      =================================================== */}

      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Transaction History
          </Typography>

          {user && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {user.name || "Unknown"} {user.emp_code ? `(${user.emp_code})` : ""}
            </Typography>
          )}
        </Box>

        <Button
          variant="text"
          size="small"
          onClick={onClose}
          startIcon={<CloseIcon fontSize="small" />}
        >
          Close
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, md: 2 } }}>
        {/* =================================================
            CURRENT BALANCE + SUMMARY (single info strip)
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountBalanceWalletOutlinedIcon fontSize="small" color="primary" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  Current Leave Balance
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  From the employee's live LeaveBalance record
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={`Planned: ${formatNumber(currentBalance.planned)}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
              <Chip
                label={`Unplanned: ${formatNumber(currentBalance.unplanned)}`}
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
              <Chip
                label={`Total: ${formatNumber(currentBalance.total)}`}
                color="success"
                sx={{ fontWeight: 800 }}
              />
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 3 }}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: "none", sm: "block" } }}
              />
            }
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <TrendingUpOutlinedIcon fontSize="small" color="success" />
              <Typography variant="body2" color="text.secondary">
                Credits this page:{" "}
                <Box component="span" sx={{ fontWeight: 800, color: "success.main" }}>
                  +{formatNumber(transactionSummary.credits)}
                </Box>
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <TrendingDownOutlinedIcon fontSize="small" color="error" />
              <Typography variant="body2" color="text.secondary">
                Debits this page:{" "}
                <Box component="span" sx={{ fontWeight: 800, color: "error.main" }}>
                  -{formatNumber(transactionSummary.debits)}
                </Box>
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Net change:{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 800,
                  color: transactionSummary.net >= 0 ? "success.main" : "error.main",
                }}
              >
                {transactionSummary.net >= 0 ? "+" : ""}
                {formatNumber(transactionSummary.net)}
              </Box>
            </Typography>
          </Stack>
        </Paper>

        {/* =================================================
            FILTERS — type on the left, cycle on the right,
            same row
        ================================================= */}

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.5}
          >
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={handleFilterChange}
              size="small"
              sx={{ flexWrap: "wrap", gap: 0.5 }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="approved">Approved</ToggleButton>
              <ToggleButton value="planned">Planned</ToggleButton>
              <ToggleButton value="unplanned">Unplanned</ToggleButton>
              <ToggleButton value="credit">Credit</ToggleButton>
              <ToggleButton value="debit">Debit</ToggleButton>
            </ToggleButtonGroup>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ ml: { md: "auto" } }}
            >
              <TextField
                type="date"
                size="small"
                label="Cycle start"
                value={cycleDate}
                onChange={handleCycleDateChange}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 170 }}
              />

              {cycleDate && (
                <Button size="small" onClick={clearCycle}>
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {totalCount} transaction{totalCount === 1 ? "" : "s"} match this filter
          </Typography>
        </Paper>

        {/* =================================================
            TABLE
        ================================================= */}

        {loading && transactions.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 450, borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 800,
                      background: "#f5f7fa",
                    },
                  }}
                >
                  <TableCell>Date</TableCell>
                  <TableCell>Bucket</TableCell>
                  <TableCell>Transaction</TableCell>
                  <TableCell align="right">Days</TableCell>
                  <TableCell align="right">Before</TableCell>
                  <TableCell align="right">Change</TableCell>
                  <TableCell align="right">After</TableCell>
                  <TableCell>Remark</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" color="text.secondary">
                        No transactions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((txn) => {
                    const change = Number(txn.balance_change ?? 0);
                    const isPositive = change > 0;
                    const isNegative = change < 0;

                    return (
                      <TableRow key={txn.id} hover>
                        <TableCell>
                          <Typography variant="body2">{formatDate(txn.created_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            #{txn.id}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={txn.balance_bucket_label || txn.balance_bucket || "Planned"}
                            color={getBucketColor(txn.balance_bucket)}
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={txn.transaction_type?.replace(/_/g, " ").toUpperCase()}
                            color={getTransactionTypeColor(txn.transaction_type)}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatNumber(txn.days)}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {formatNumber(txn.balance_before)}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              color: isPositive
                                ? "success.main"
                                : isNegative
                                ? "error.main"
                                : "text.secondary",
                            }}
                          >
                            {isPositive ? "+" : ""}
                            {formatNumber(change)}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {formatNumber(txn.balance_after)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              maxWidth: 250,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {txn.remarks || "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* =================================================
            PAGINATION
        ================================================= */}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}